import { action, computed, makeAutoObservable, observable } from "mobx"
import { Option, option2map } from "polaris-enhance"

import APILabelFee from "@/api/labelFee"
import APIReturnLanguage from "@/api/returnpage/language"
import APISetting, {
  type IntegrationListItem as IndirectItem,
  statusEnum,
} from "@/api/setting"
import { IntegrationItem, IntegrationListItem } from "@/api/setting/type"
export default class CacheStore {
  constructor() {
    // 使用这个才会在 MobX 6 上才会更新视图
    makeAutoObservable(this)
  }

  @observable countryOptions: Option[] = []
  @action.bound
  setCountryOptions(val: Option[]) {
    this.countryOptions = val
  }
  @action.bound
  async getCountryOptions() {
    const res = await APILabelFee.getMarketCountryOptions({ id: -1 })
    if (res.code === 1000 && res.data) {
      const data = res.data.map((item) => ({
        label: item.name,
        value: item.code,
      }))
      this.setCountryOptions(data)
    }
  }

  @computed
  get carrierListMap() {
    const map: Record<string, IntegrationItem> = {}
    this.carrierList[0]?.list.forEach((item) => {
      map[item.id] = item
    })
    return map
  }

  @computed
  get reasonListMap() {
    return option2map(this.reasonList)
  }

  @computed
  get addressListMap() {
    return option2map(this.addressList)
  }

  @observable carrierList: IntegrationListItem[] = []

  @action.bound
  setCarrierList(list: IntegrationListItem[]) {
    this.carrierList = list
  }

  @action.bound
  async getCarrierList() {
    const res = await APISetting.getDirectIntegrationList({ is_workflow: true })
    if (res.code === 1000) {
      this.setCarrierList(res.data || [])
    }
  }

  @observable addressList: Option<string>[] = []

  @action.bound
  setAddressList(list: Option<string>[]) {
    this.addressList = list
  }

  @action.bound
  async getAddressList() {
    const res = await APISetting.getAllAddressList()
    if (res.code === 1000 && res.data) {
      this.setAddressList(
        res.data?.map((item) => {
          return { value: String(item.id), label: item.name }
        }) ?? [],
      )
    }
  }

  @observable reasonList: Option<string>[] = []

  @action.bound
  setReasonList(list: Option<string>[]) {
    this.reasonList = list
  }

  @action.bound
  async getReasonList() {
    const res = await APISetting.getReasonNew({})

    if (res.code === 1000 && res.data) {
      const data =
        res.data?.map((item) => ({
          label: item.content,
          value: item.id + "",
        })) ?? []
      this.setReasonList(data)
    }
  }

  /** 与直连 carrierList 区分；请求完成前 indirectActive 为 true 以免误显 Banner */
  @observable
  indirectList: IndirectItem[] = []

  @observable dhlReceiverOpts: { label: string; value: string }[] = []

  @observable indirectActive = true

  @action.bound
  setIndirectList(list: IndirectItem[]) {
    this.indirectList = list
  }

  @action.bound
  setDhlReceiverOpts(options: { label: string; value: string }[]) {
    this.dhlReceiverOpts = options
  }

  /** 间接集成列表；有 Active 再拉 DHL。可与 getCarrierList 等 Promise.all */
  @action.bound
  async getIndirectDhl() {
    const res = await APISetting.getIntegrationList({ is_workflow: true })
    const list = res?.data ?? []
    this.setIndirectList(list)
    const isActive = list.some((i) => i?.status === statusEnum.Active)
    this.indirectActive = isActive

    if (isActive) {
      const dhlRes = await APISetting.getDHLLocation()
      if (dhlRes?.data) {
        this.setDhlReceiverOpts(
          dhlRes.data.map((i) => ({
            label: i.receiverId,
            value: i.receiverId,
          })),
        )
      } else {
        this.setDhlReceiverOpts([])
      }
    } else {
      this.setDhlReceiverOpts([])
    }
  }

  @computed
  get defaultLanguageName() {
    return this.languageList[0]?.label || "English (default)"
  }

  @computed
  get defaultLanguage() {
    return this.languageList[0]?.value
  }

  @observable languageList: Option<string>[] = []
  @action.bound
  setLanguageList(list: Option<string>[]) {
    this.languageList = list
  }
  @action.bound
  async getLanguageList() {
    const res = await APIReturnLanguage.getLanguageList()
    if (res.data.code === 1000) {
      const data = res.data.data.map((item) => ({
        label: item.ename,
        value: item.code,
      }))
      this.setLanguageList(data)
    }
  }
}
